import type { ReactElement } from 'react'
import type { Capability } from '~/capabilities'
import { Account } from '~/pages/Account'
import { Allocation } from '~/pages/Allocation'
import { Dashboard } from '~/pages/Dashboard'
import { Health } from '~/pages/Health'
import { Holdings } from '~/pages/Holdings'
import { ImportHistory } from '~/pages/ImportHistory'
import { Manual } from '~/pages/Manual'
import { Performance } from '~/pages/Performance'
import { Risk } from '~/pages/Risk'
import { Settings } from '~/pages/Settings'
import { Sources } from '~/pages/Sources'
import { Units } from '~/pages/Units'
import { Warrants } from '~/pages/Warrants'
import { PRIVATE_HOLDINGS_DEFAULTS } from '~/state/private-holdings'
import { useUnitsOverview } from '~/state/queries'
import { useWarrants } from '~/state/warrants'

export type { Capability }

export interface Tab {
  id: string
  label: string
  /** The address this tab is read at, so it can be linked to and returned to. */
  path: string
  render: () => ReactElement
  /** Absent for a tab every household can use; named for one that some cannot. */
  gate?: Capability
}

export interface Destination {
  path: string
  label: string
  tabs: readonly Tab[]
}

interface TabSpec {
  id: string
  label: string
  render: () => ReactElement
  gate?: Capability
}

/**
 * A tab is a segment under its destination rather than a query on it, because
 * every other address in this product is a path and a tab is a place in it, not
 * a setting applied to one. The first tab is the destination itself: it keeps
 * `/holdings` meaning what it has always meant instead of bouncing to a longer
 * spelling of the same page.
 */
function destination(path: string, label: string, specs: readonly TabSpec[]): Destination {
  const stem = path === '/' ? '' : path
  return {
    path,
    label,
    tabs: specs.map((spec, index) => ({
      ...spec,
      path: index === 0 ? path : `${stem}/${spec.id}`,
    })),
  }
}

export const DESTINATIONS: readonly Destination[] = [
  destination('/', 'Overview', [
    { id: 'overview', label: 'Overview', render: () => <Dashboard /> },
  ]),
  destination('/holdings', 'Holdings', [
    { id: 'all', label: 'All holdings', render: () => <Holdings /> },
    { id: 'allocation', label: 'Allocation', render: () => <Allocation /> },
    { id: 'warrants', label: 'Warrants', render: () => <Warrants />, gate: 'warrants' },
  ]),
  destination('/performance', 'Performance', [
    { id: 'returns', label: 'Returns', render: () => <Performance /> },
    { id: 'risk', label: 'Risk', render: () => <Risk /> },
    { id: 'units', label: 'In units', render: () => <Units />, gate: 'units' },
  ]),
  destination('/sources', 'Sources', [
    { id: 'connections', label: 'Connections', render: () => <Sources /> },
    { id: 'imports', label: 'Import history', render: () => <ImportHistory /> },
    { id: 'health', label: 'Health', render: () => <Health /> },
  ]),
]

export const LOGIN_PATH = '/login'
export const ACCOUNT_PATH = '/account'
export const SETTINGS_PATH = '/settings'
export const MANUAL_PATH = '/manual'

/**
 * Pages the four destinations do not offer. The account and its settings are
 * behind the avatar; hand entry is how a holding arrived rather than a kind of
 * holding, so it keeps an address without taking a place in the navigation.
 */
export const ASIDES: Readonly<Record<string, (() => ReactElement) | undefined>> = {
  [ACCOUNT_PATH]: () => <Account />,
  [SETTINGS_PATH]: () => <Settings />,
  [MANUAL_PATH]: () => <Manual />,
}

/**
 * Where an address that has moved is read instead.
 *
 * Seven of the eleven destinations became a tab, and every one of them is in
 * somebody's bookmarks. A first tab is entered here too: its own segment is a
 * spelling people will guess, and it lands on the canonical address rather than
 * on the Overview.
 */
const MOVED: Readonly<Record<string, string | undefined>> = {
  '/allocation': '/holdings/allocation',
  '/warrants': '/holdings/warrants',
  '/risk': '/performance/risk',
  '/units': '/performance/units',
  '/health': '/sources/health',
  ...Object.fromEntries(
    DESTINATIONS.flatMap((one) => {
      const [first] = one.tabs
      const stem = one.path === '/' ? '' : one.path
      return first === undefined ? [] : [[`${stem}/${first.id}`, one.path]]
    }),
  ),
}

export function movedTo(path: string): string | undefined {
  return MOVED[path]
}

export interface Landing {
  destination: Destination
  tab: Tab
}

/** The destination and tab an address shows; one nothing claims shows the first. */
export function landingFor(path: string): Landing {
  for (const destination of DESTINATIONS) {
    const tab = destination.tabs.find((candidate) => candidate.path === path)
    if (tab !== undefined) return { destination, tab }
  }

  const [home] = DESTINATIONS
  const first = home?.tabs[0]
  if (home === undefined || first === undefined) throw new Error('no destinations')
  return { destination: home, tab: first }
}

/** Whether this address is one the app answers itself rather than a link away. */
export function isAppPath(path: string): boolean {
  if (path === LOGIN_PATH) return true
  if (Object.hasOwn(ASIDES, path)) return true
  if (movedTo(path) !== undefined) return true
  return DESTINATIONS.some((one) => one.tabs.some((tab) => tab.path === path))
}

/**
 * Whether a tab that only means something for some households means something
 * for this one. Both reuse the query a full page would ask for — `useWarrants`
 * already carries every grant, and the units overview already answers for the
 * net worth a unit would divide — rather than invent a second source of truth
 * for the same fact.
 *
 * `enabled` is false until a household is known, so a visitor who has not
 * signed in, or has no household yet, never triggers either request. Units
 * are asked for against the default settings rather than the household's own:
 * what is being asked is whether there is a net worth at all, not the figure a
 * switch on the Units page would compute.
 */
export function useCapabilities(enabled: boolean): Readonly<Record<Capability, boolean>> {
  const warrants = useWarrants(enabled)
  const units = useUnitsOverview(PRIVATE_HOLDINGS_DEFAULTS, 'lookThrough', enabled)

  return {
    warrants: (warrants.data?.grants.length ?? 0) + (warrants.data?.unvaluable?.length ?? 0) > 0,
    // Not "has a unit". The yardstick is chosen on this page, so a tab gated on
    // one already existing put the only door to it behind the door — which is
    // how PRO-137 shipped a view no household could ever reach. What the tab
    // needs is something to divide, and an overview that answered at all is
    // exactly that: it refuses a household with no positions to value.
    units: units.data !== undefined,
  }
}

/**
 * The tabs a destination offers: one gated on a capability the household does
 * not have is left out, unless it is the tab already open. Hiding is about
 * what the menu offers to go to, not about what a page already reached — by
 * address, or mid-session by losing the last row that earned it its place —
 * is allowed to keep showing.
 */
export function visibleTabs(
  destination: Destination,
  capabilities: Readonly<Record<Capability, boolean>>,
  activeTabId: string,
): readonly Tab[] {
  return destination.tabs.filter(
    (tab) => tab.gate === undefined || capabilities[tab.gate] || tab.id === activeTabId,
  )
}
